// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getFocalPointStyles, parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { responsiveStyleRules } from "@insite/client-framework/Common/Utilities/responsive";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { LazyImagePresentationProps } from "@insite/mobius/LazyImage";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Video, { VideoStyles } from "@stoneage/App/Components/Media/Video";
import {
    fetchBackgroundImg,
    getObjectPositionStyles,
    isValidVideoUrl,
    xClassNames,
    xScreen,
} from "@stoneage/XDL/Utilities";
import * as cssLinter from "css";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";
import LazyPicture from "@stoneage/MobiusOverrides/LazyImage/LazyPicture";

const enum fields {
    background = "background",
    image = "image",
    focalPoint = "focalPoint",
    backgroundColor = "backgroundColor",
    heading = "heading",
    minimumHeight = "minimumHeight",
    subheading = "subheading",
    buttonLabel = "buttonLabel",
    buttonLink = "buttonLink",
    buttonVariant = "variant",
    bannerWidth = "bannerWidth",
    imageOverlay = "imageOverlay",
    partialOverlay = "partialOverlay",
    partialOverlayPositioning = "partialOverlayPositioning",
    disableButton = "disableButton",
    contentPadding = "contentPadding",
    centerTextVertically = "centerTextVertically",
    responsiveFontSizes = "responsiveFontSizes",
    customFontSizes = "customFontSizes",
    h1FontSize = "h1FontSize",
    h2FontSize = "h2FontSize",
    h3FontSize = "h3FontSize",
    h4FontSize = "h4FontSize",
    h5FontSize = "h5FontSize",
    h6FontSize = "h6FontSize",
    normalFontSize = "normalFontSize",
    customCSS = "customCSS",
    // XNG-changes
    description = "description",
    imageTablet = "imageTablet",
    imageMobile = "imageMobile",
    secondaryButtonLabel = "secondaryButtonLabel",
    secondaryButtonLink = "secondaryButtonLink",
    secondaryButtonVariant = "secondaryButtonVariant",
    buttonSizeVariant = "buttonSizeVariant",
    secondaryButtonSizeVariant = "secondaryButtonSizeVariant",
    videoURL = "videoURL",
    muted = "muted",
    controls = "controls",
    repeatable = "repeatable",
    contentPaddingOnMobile = "contentPaddingOnMobile",
    contentBackgroundColorOnMobile = "contentBackgroundColorOnMobile",
    maxHeightMediaSourceOnTablet = "maxHeightMediaSourceOnTablet",
    maxHeightMediaSourceOnMobile = "maxHeightMediaSourceOnMobile",
}

interface OwnProps extends WidgetProps {
    fields: {
        // xng-change
        [fields.background]: "image" | "color" | "video";
        [fields.image]: string;
        [fields.focalPoint]:
            | "topLeft"
            | "topCenter"
            | "topRight"
            | "centerLeft"
            | "center"
            | "centerRight"
            | "bottomLeft"
            | "bottomCenter"
            | "bottomRight";
        [fields.backgroundColor]: string;
        [fields.heading]: string;
        [fields.minimumHeight]: "1/4 viewport" | "1/2 viewport" | "3/4 viewport" | "fullViewport";
        [fields.subheading]: string;
        [fields.buttonLabel]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.buttonVariant]: "primary" | "secondary" | "tertiary";
        [fields.imageOverlay]: string;
        [fields.partialOverlay]: boolean;
        [fields.partialOverlayPositioning]: "top" | "middle" | "bottom";
        [fields.disableButton]: boolean;
        [fields.contentPadding]: number;
        [fields.centerTextVertically]: boolean;
        [fields.responsiveFontSizes]: boolean;
        [fields.customFontSizes]: boolean;
        [fields.h1FontSize]: number;
        [fields.h2FontSize]: number;
        [fields.h3FontSize]: number;
        [fields.h4FontSize]: number;
        [fields.h5FontSize]: number;
        [fields.normalFontSize]: number;
        [fields.customCSS]: string;
        // XNG-Changes
        [fields.description]: string;
        [fields.imageTablet]: string;
        [fields.imageMobile]: string;
        [fields.secondaryButtonLabel]: string;
        [fields.secondaryButtonLink]: LinkFieldValue;
        [fields.secondaryButtonVariant]: "primary" | "secondary";
        [fields.buttonSizeVariant]: "small" | "medium" | "large";
        [fields.secondaryButtonSizeVariant]: "small" | "medium" | "large";
        [fields.videoURL]: string;
        [fields.muted]: boolean;
        [fields.controls]: boolean;
        [fields.repeatable]: boolean;
        [fields.contentPaddingOnMobile]: string;
        [fields.contentBackgroundColorOnMobile]: string;
        [fields.maxHeightMediaSourceOnTablet]: string;
        [fields.maxHeightMediaSourceOnMobile]: string;
    };
    extendedStyles?: BannerStyles;
}

export interface BannerStyles {
    wrapper?: InjectableCss;
    overlayWrapper?: InjectableCss;
    /**
     * @deprecated Use the `bannerButton` property instead.
     */
    slideCenteringWrapperStyles?: InjectableCss;
    bannerLink?: LinkPresentationProps;
    bannerButton?: ButtonPresentationProps;
    // XNG changes
    pageContainer?: InjectableCss;
    heading?: TypographyPresentationProps;
    subheading?: TypographyPresentationProps;
    description?: TypographyPresentationProps;
    secondaryBannerButton?: ButtonPresentationProps;
    buttonsWrapper?: InjectableCss;
    videoWrapper?: InjectableCss;
    video?: VideoStyles;
    imageWrapper?: InjectableCss;
    image?: LazyImagePresentationProps;
}

export const bannerStyles: BannerStyles = {
    wrapper: {
        css: css`
            width: 100%;
            display: flex;
        `,
    },
    overlayWrapper: {
        css: css`
            width: 100%;
            color: white;
            text-align: center;
        `,
    },
};

export const Banner: FC<OwnProps> = ({ fields, extendedStyles }) => {
    const { url, title } = useGetLink(fields.buttonLink);
    const history = useHistory();
    const { isInShell } = useShellContext();
    // XNG-Changes
    const secondaryButton = useGetLink(fields.secondaryButtonLink);
    // XNG-Changes
    const { isAuthenticated } = useAppSelector(state => ({
        isAuthenticated:
            (state.context.session.isAuthenticated || state.context.session.rememberMe) &&
            !state.context.session.isGuest,
    }));

    // XNG-Changes
    const onClick = (url?: string) => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    const responsiveBackgroundImageStyle = css`
        background-image: ${fetchBackgroundImg(fields.image)};
        ${xScreen("lte", "md")} {
            background-image: ${fetchBackgroundImg(fields.imageTablet || fields.image)};
        }
        ${xScreen("lte", "sm")} {
            flex-direction: column;
            background-image: none;
        }
    `;

    const backgroundStyles =
        fields.background === "image"
            ? css`
                  ${responsiveBackgroundImageStyle}
                  background-size: cover;
              `
            : css`
                  background-color: ${fields.backgroundColor};
              `;

    const focalPointStyles = getFocalPointStyles(fields.focalPoint);

    let minimumHeightStyles;
    switch (fields.minimumHeight) {
        case "1/4 viewport":
            minimumHeightStyles = "min-height: 25vh;";
            break;
        case "1/2 viewport":
            minimumHeightStyles = "min-height: 50vh;";
            break;
        case "3/4 viewport":
            minimumHeightStyles = "min-height: 75vh;";
            break;
        case "fullViewport":
            minimumHeightStyles = "min-height: 100vh;";
            break;
    }

    let overlayPositioningStyles;
    if (!fields.partialOverlay) {
        overlayPositioningStyles = "align-items: stretch;";
    } else if (fields.partialOverlayPositioning === "top") {
        overlayPositioningStyles = "align-items: flex-start;";
    } else if (fields.partialOverlayPositioning === "middle") {
        overlayPositioningStyles = "align-items: center;";
    } else if (fields.partialOverlayPositioning === "bottom") {
        overlayPositioningStyles = "align-items: flex-end;";
    }

    const styles = useMergeStyles("banner", bannerStyles, extendedStyles);

    let fontSizeStyles;
    if (fields.responsiveFontSizes || fields.customFontSizes) {
        fontSizeStyles = responsiveStyleRules(fields.responsiveFontSizes, fields.customFontSizes ? fields : undefined);
    }

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    const wrapperStyles = {
        css: css`
            ${styles.wrapper?.css || ""}
            ${backgroundStyles}
            ${focalPointStyles}
            ${minimumHeightStyles}
            ${overlayPositioningStyles}
            ${fontSizeStyles}
            // xng-change
            display: flex;
            position: relative;
            ${fields.background === "video" && styles.videoWrapper?.css}
        `,
    };

    // XNG-Changes: the focal point only reaches the desktop background-image. On tablet/mobile the banner
    // renders a real <img> through LazyPicture, so translate the same field into `object-position`.
    const imageWrapperStyles = {
        css: css`
            ${styles.imageWrapper?.css || ""}
            &&& img {
                width: 100%;
                height: 100%;
                object-fit: cover;
                ${getObjectPositionStyles(fields.focalPoint)}
            }
        `,
    };

    const overlayWrapperStyles = {
        css: css`
            ${styles.overlayWrapper?.css || ""}
            background-color: ${fields.background === "image" ? fields.imageOverlay : ""};
            padding: ${fields.contentPadding}px;
            // XNG-Changes
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            z-index: 1;
            pointer-events: none;
            button,
            a {
                pointer-events: auto;
            }

            ${fields.centerTextVertically &&
            css`
                display: flex;
                align-items: center;
                justify-content: center;
            `}

            ${fields.background === "video" &&
            css`
                width: auto;
            `}

            ${xScreen("lte", "md")} {
                pointer-events: auto;
                position: static;
                transform: unset;
                padding: ${fields.contentPaddingOnMobile};
                background-color: ${fields.contentBackgroundColorOnMobile};
            }
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper className="banner-wrapper" {...wrapperStyles} role="banner">
                {/* xng-change */}
                {fields.background === "video" && (
                    <StyledWrapper
                        {...styles.videoWrapper}
                        data-test-selector="video-slide"
                        {...{
                            maxHeightMediaSourceOnTablet: fields.maxHeightMediaSourceOnTablet,
                            maxHeightMediaSourceOnMobile: fields.maxHeightMediaSourceOnMobile,
                        }}
                    >
                        <Video
                            autoPlay
                            controls={fields.controls}
                            muted={fields.muted}
                            src={fields.videoURL}
                            repeat={fields.repeatable}
                            extendedStyles={styles.video}
                        />
                    </StyledWrapper>
                )}

                {/* xng-change */}
                {fields.background === "image" && (fields.imageMobile || fields.imageTablet || fields.image) && (
                    <StyledWrapper
                        {...imageWrapperStyles}
                        {...{
                            maxHeightMediaSourceOnTablet: fields.maxHeightMediaSourceOnTablet,
                            maxHeightMediaSourceOnMobile: fields.maxHeightMediaSourceOnMobile,
                        }}
                        data-test-selector="imageWrapper"
                    >
                        <LazyPicture
                            largeImageSrc={fields.image}
                            mediumImageSrc={fields.imageTablet || fields.image}
                            smallImageSrc={fields.imageMobile || fields.imageTablet || fields.image}
                            altText={fields.heading}
                        />
                    </StyledWrapper>
                )}

                <StyledWrapper className="banner-overlay-wrapper" {...overlayWrapperStyles}>
                    <StyledWrapper {...styles.pageContainer}>
                        <StyledWrapper
                            className={xClassNames.append([
                                "banner-center-wrapper",
                                isAuthenticated && "authenticated",
                            ])}
                            {...styles.slideCenteringWrapperStyles}
                        >
                            {fields.subheading && (
                                <Typography
                                    className="banner-subheading"
                                    {...styles.subheading}
                                    data-test-selector="Banner-Subheading"
                                >
                                    {parse(fields.subheading, parserOptions)}
                                </Typography>
                            )}
                            {fields.heading && (
                                <Typography
                                    className="banner-heading"
                                    {...styles.heading}
                                    data-test-selector="Banner-Heading"
                                >
                                    {parse(fields.heading, parserOptions)}
                                </Typography>
                            )}
                            {fields.description && (
                                <Typography
                                    className="banner-description"
                                    {...styles.description}
                                    data-test-selector="Banner-Description"
                                >
                                    {parse(fields.description, parserOptions)}
                                </Typography>
                            )}
                            <StyledWrapper {...styles.buttonsWrapper} data-test-selector="Banner-ButtonsWrapper">
                                {!fields.disableButton && (
                                    <Button
                                        {...styles.bannerButton}
                                        variant={fields.variant}
                                        // XNG-Changes
                                        sizeVariant={fields.buttonSizeVariant}
                                        onClick={() => onClick(url)}
                                    >
                                        {fields.buttonLabel || title || url}
                                    </Button>
                                )}
                                {(fields.secondaryButtonLabel || secondaryButton.url) && (
                                    <Button
                                        {...styles.bannerButton}
                                        variant={fields.secondaryButtonVariant}
                                        sizeVariant={fields.secondaryButtonSizeVariant}
                                        onClick={() => {
                                            onClick(secondaryButton.url);
                                        }}
                                        {...{ hasPrimaryButton: fields.disableButton }}
                                    >
                                        {fields.secondaryButtonLabel || secondaryButton.title || secondaryButton.url}
                                    </Button>
                                )}
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.banner-wrapper {
}

.banner-overlay-wrapper {
}

.banner-center-wrapper {
}

.banner-heading {
}

.banner-subheading {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const settingsTab = {
    displayName: "Settings",
    sortOrder: 1,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 2,
};

const banner: WidgetModule = {
    component: Banner,
    definition: {
        group: "Basic",
        icon: "image-landscape",
        fieldDefinitions: [
            {
                fieldType: "Contextual",
                name: fields.background,
                displayName: "Background",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Image", value: "image" },
                    { displayName: "Color", value: "color" },
                    // xng-change
                    { displayName: "Video", value: "video" },
                ],
                defaultValue: "image",
                hideEmptyOption: true,
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.image,
                displayName: "Image For Desktop Viewport",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                displayName: "Image For Tablet Viewport",
                name: fields.imageTablet,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.imageMobile,
                displayName: "Image For Mobile Viewport",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.imageOverlay,
                displayName: "Image Color Overlay",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.partialOverlay,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.partialOverlayPositioning,
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Top", value: "top" },
                    { displayName: "Middle", value: "middle" },
                    { displayName: "Bottom", value: "bottom" },
                ],
                hideEmptyOption: true,
                defaultValue: "bottom",
                isVisible: widget => widget.fields.partialOverlay,
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.focalPoint,
                displayName: "Focal Point",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Top Left", value: "topLeft" },
                    { displayName: "Top Center", value: "topCenter" },
                    { displayName: "Top Right", value: "topRight" },
                    { displayName: "Center Left", value: "centerLeft" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Center Right", value: "centerRight" },
                    { displayName: "Bottom Left", value: "bottomLeft" },
                    { displayName: "Bottom Center", value: "bottomCenter" },
                    { displayName: "Bottom Right", value: "bottomRight" },
                ],
                defaultValue: "center",
                hideEmptyOption: true,
                isVisible: widget => widget.fields.background === "image",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Color",
                defaultValue: "black",
                isVisible: widget => widget.fields.background === "color",
                tab: basicTab,
            },
            // <xng-change>
            {
                fieldType: "Contextual",
                name: fields.contentBackgroundColorOnMobile,
                displayName: "Content Background Color on Mobile",
                editorTemplate: "ColorPickerField",
                defaultValue: "#000000",
            },
            {
                fieldType: "Contextual",
                name: fields.videoURL,
                displayName: "Video URL",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isRequired: true,
                isVisible: widget => widget.fields.background === "video",
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }
                    const isValid = isValidVideoUrl(value, true);
                    return !isValid ? "Url isn't valid" : "";
                },
            },
            {
                fieldType: "Contextual",
                name: fields.muted,
                displayName: "Muted",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isVisible: widget => widget.fields.background === "video",
            },
            {
                fieldType: "Contextual",
                name: fields.repeatable,
                displayName: "Repeatable",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isVisible: widget => widget.fields.background === "video",
            },
            {
                fieldType: "Contextual",
                name: fields.controls,
                displayName: "Show Controls",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                isVisible: widget => widget.fields.background === "video",
            },
            // </xng-change>
            {
                fieldType: "Contextual",
                name: fields.heading,
                editorTemplate: "RichTextField",
                displayName: "Heading",
                defaultValue: "",
                extendedConfig: { height: 80 },
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.subheading,
                editorTemplate: "RichTextField",
                displayName: "Subheading",
                defaultValue: "",
                extendedConfig: { height: 80 },
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.description,
                editorTemplate: "RichTextField",
                displayName: "Description",
                defaultValue: "",
                extendedConfig: { height: 170 },
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.buttonLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                defaultValue: "",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.buttonLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: basicTab,
            },
            {
                name: fields.buttonVariant,
                displayName: "Button Variant",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                ],
                hideEmptyOption: true,
                defaultValue: "primary",
                fieldType: "Contextual",
                tab: basicTab,
            },
            // XNG-Changes
            {
                name: fields.buttonSizeVariant,
                displayName: "Button Size Variant",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Small", value: "small" },
                    { displayName: "Medium", value: "medium" },
                    { displayName: "Large", value: "large" },
                ],
                hideEmptyOption: true,
                defaultValue: "medium",
                fieldType: "Contextual",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.secondaryButtonLabel,
                editorTemplate: "TextField",
                displayName: "Secondary Button Label",
                defaultValue: "",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.secondaryButtonLink,
                displayName: "Secondary Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: basicTab,
            },
            {
                name: fields.secondaryButtonVariant,
                displayName: "Secondary Button Variant",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                ],
                hideEmptyOption: true,
                defaultValue: "secondary",
                fieldType: "Contextual",
                tab: basicTab,
            },
            {
                name: fields.secondaryButtonSizeVariant,
                displayName: "Secondary Button Size Variant",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Small", value: "small" },
                    { displayName: "Medium", value: "medium" },
                    { displayName: "Large", value: "large" },
                ],
                hideEmptyOption: true,
                defaultValue: "medium",
                fieldType: "Contextual",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.minimumHeight,
                displayName: "Minimum Banner Height",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "1/4 Viewport", value: "1/4 viewport" },
                    { displayName: "1/2 Viewport", value: "1/2 viewport" },
                    { displayName: "3/4 Viewport", value: "3/4 viewport" },
                    { displayName: "Full Viewport", value: "fullViewport" },
                ],
                defaultValue: "1/4 viewport",
                hideEmptyOption: true,
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.disableButton,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: basicTab,
            },
            {
                name: fields.responsiveFontSizes,
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                tab: settingsTab,
                defaultValue: true,
            },
            {
                fieldType: "Contextual",
                name: fields.contentPadding,
                editorTemplate: "IntegerField",
                defaultValue: 50,
                tab: basicTab,
            },
            // xng-change
            {
                fieldType: "Contextual",
                name: fields.contentPaddingOnMobile,
                editorTemplate: "TextField",
                displayName: "Content Padding on Mobile",
                placeholder: "e.g. 10px, 24px 16px",
                defaultValue: "24px 0",
                validate: (value: string | null) => {
                    if (!value) {
                        return null;
                    }
                    return CSS.supports("padding", value) ? null : `Invalid Input`;
                },
            },
            // xng-change
            {
                fieldType: "Contextual",
                name: fields.maxHeightMediaSourceOnTablet,
                editorTemplate: "TextField",
                displayName: "Max Height for Media Source on Tablet",
                placeholder: "e.g. 100px",
                defaultValue: "271px",
                validate: (value: string | null) => {
                    if (!value) {
                        return null;
                    }
                    return CSS.supports("max-height", value) ? null : `Invalid Input`;
                },
            },
            {
                fieldType: "Contextual",
                name: fields.maxHeightMediaSourceOnMobile,
                editorTemplate: "TextField",
                displayName: "Max Height for Media Source on Mobile",
                placeholder: "e.g. 100px",
                defaultValue: "127px",
                validate: (value: string | null) => {
                    if (!value) {
                        return null;
                    }
                    return CSS.supports("max-height", value) ? null : `Invalid Input`;
                },
            },
            {
                fieldType: "Contextual",
                name: "centerTextVertically",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: basicTab,
            },
            {
                name: fields.customFontSizes,
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                tab: settingsTab,
                defaultValue: false,
            },
            {
                name: fields.normalFontSize,
                editorTemplate: "IntegerField",
                fieldType: "Contextual",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h1FontSize,
                editorTemplate: "IntegerField",
                fieldType: "Contextual",
                tab: settingsTab,
                min: 1,
                defaultValue: 40,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h2FontSize,
                editorTemplate: "IntegerField",
                fieldType: "Contextual",
                tab: settingsTab,
                min: 1,
                defaultValue: 32,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h3FontSize,
                editorTemplate: "IntegerField",
                fieldType: "Contextual",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h4FontSize,
                editorTemplate: "IntegerField",
                fieldType: "Contextual",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h5FontSize,
                editorTemplate: "IntegerField",
                fieldType: "Contextual",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h6FontSize,
                editorTemplate: "IntegerField",
                fieldType: "Contextual",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "Contextual",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default banner;
